import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  FaArrowLeft
} from 'react-icons/fa';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

import SalesPageHeader from '../components/sales/SalesPageHeader';
import { useSalesEmbedded } from '../hooks/useSalesEmbedded';
import PortalBackground from '../components/PortalBackground';

const DesignPricing = ({ embedded: embeddedProp, overrideUserId = null }) => {
  const embedded = useSalesEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  // In the CRM Portal View this page is scoped to the client's user; otherwise
  // it reads/writes the signed-in user's own pricing.
  const targetUid = overrideUserId || currentUser?.uid;
  
  // State for pricing data
  const [pricingData, setPricingData] = useState({
    lastUpdated: null
  });
  
  // State for Design & Feasibility Contract pricing
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [designFee, setDesignFee] = useState(0);
  const [pricingTier, setPricingTier] = useState('');
  
  // Design & Feasibility Contract pricing tiers
  const pricingTiers = [
    { min: 75000, max: 150000, percentage: 8, label: '$75k - $150k' },
    { min: 151000, max: 250000, percentage: 7.5, label: '$151k - $250k' },
    { min: 251000, max: 350000, percentage: 7, label: '$251k - $350k' },
    { min: 351000, max: 600000, percentage: 6.5, label: '$351k - $600k' },
    { min: 600001, max: Infinity, percentage: 6, label: '$600k+' }
  ];
  
  
  // Load pricing data from Firestore
  useEffect(() => {
    const loadPricingData = async () => {
      if (!targetUid) return;

      try {
        const userDocRef = doc(db, 'users', targetUid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && userDoc.data().designPricing) {
          const data = userDoc.data().designPricing;
          setPricingData(data);
          if (data.minPrice) {
            setMinPrice(data.minPrice);
          }
          if (data.maxPrice) {
            setMaxPrice(data.maxPrice);
          }
          if (data.designFee) {
            setDesignFee(data.designFee);
          }
          if (data.pricingTier) {
            setPricingTier(data.pricingTier);
          }
        }
      } catch (error) {
        console.error('Error loading pricing data:', error);
      }
    };
    
    loadPricingData();
  }, [targetUid]);
  
  
  // Calculate design fee based on price range midpoint
  useEffect(() => {
    if (minPrice && maxPrice) {
      const min = parseFloat(minPrice);
      const max = parseFloat(maxPrice);
      if (min > 0 && max > 0 && max >= min) {
        const midpoint = (min + max) / 2;
        const tier = pricingTiers.find(tier => midpoint >= tier.min && midpoint <= tier.max);
        if (tier) {
          const fee = (midpoint * tier.percentage) / 100;
          setDesignFee(Math.round(fee));
          setPricingTier(tier.label);
        }
      }
    } else {
      setDesignFee(0);
      setPricingTier('');
    }
  }, [minPrice, maxPrice]);

  // Auto-save design fee data when it changes
  useEffect(() => {
    if (minPrice && maxPrice && designFee > 0 && targetUid) {
      const saveData = async () => {
        try {
          const userDocRef = doc(db, 'users', targetUid);
          const dataToSave = {
            ...pricingData,
            minPrice: minPrice,
            maxPrice: maxPrice,
            designFee: designFee,
            pricingTier: pricingTier,
            lastUpdated: new Date().toISOString()
          };
          
          await updateDoc(userDocRef, {
            designPricing: dataToSave
          });
          
          setPricingData(dataToSave);
        } catch (error) {
          console.error('Error auto-saving pricing data:', error);
        }
      };
      
      // Debounce the save to avoid too many calls
      const timeoutId = setTimeout(saveData, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [minPrice, maxPrice, designFee, pricingTier, targetUid]);
  
  

  return (
    <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <SalesPageHeader title="Design Pricing" />}

      {/* Top Row: Projected Price and Design Fee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Projected Price Range Card */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6`}>
          <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Projected Project Price Range
          </h3>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className={`w-full pl-8 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Min price"
                min="0"
                step="1000"
              />
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              -
            </div>
            <div className="relative flex-1">
              <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className={`w-full pl-8 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="Max price"
                min="0"
                step="1000"
              />
            </div>
          </div>
          {(() => {
            const min = parseFloat(minPrice);
            const max = parseFloat(maxPrice);
            if (!(min > 0) || !(max > 0) || max < min) return null;
            const midpoint = Math.round((min + max) / 2);
            return (
              <div className="mt-4 text-center">
                <p className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Midpoint
                </p>
                <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                  ${midpoint.toLocaleString()}
                </p>
              </div>
            );
          })()}
        </div>

        {/* Design Fee Card */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6`}>
          <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Recommended Design Fee
          </h3>
          {designFee > 0 ? (
            <div className="text-center">
              <div className={`text-4xl font-bold text-red-600 dark:text-red-400 mb-2`}>
                ${designFee.toLocaleString()}
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
                Based on {pricingTier} tier
              </p>
              <div className={`p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Calculation:</strong> Midpoint of range (${minPrice && maxPrice ? ((parseFloat(minPrice) + parseFloat(maxPrice)) / 2).toLocaleString() : '0'}) × {pricingTiers.find(tier => tier.label === pricingTier)?.percentage}%
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className={`text-2xl font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'} mb-2`}>
                Enter price range
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                to calculate design fee
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Structure Table - Full Width */}
      <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6`}>
        <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Pricing Structure
        </h3>
        <div className={`overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800`}>
          <table className="w-full">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Project Range
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Fee Percentage
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Example Fee
                </th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {pricingTiers.map((tier, index) => {
                const examplePrice = tier.max === Infinity ? 800000 : (tier.min + tier.max) / 2;
                const exampleFee = Math.round((examplePrice * tier.percentage) / 100);
                const isCurrentTier = minPrice && maxPrice && (() => {
                  const min = parseFloat(minPrice);
                  const max = parseFloat(maxPrice);
                  const midpoint = (min + max) / 2;
                  return midpoint >= tier.min && midpoint <= tier.max;
                })();
                
                return (
                  <tr key={index} className={isCurrentTier ? `${darkMode ? 'bg-red-900/20' : 'bg-red-50'} border-l-4 border-red-500` : ''}>
                    <td className={`px-4 py-3 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {tier.label}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {tier.percentage}%
                    </td>
                    <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ${exampleFee.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>



      </div>
    </div>
  );
};

export default DesignPricing;
